import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession, useSignIn } from '@/lib/queries';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const signIn = useSignIn();
  const { data: session, isLoading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const redirect = new URLSearchParams(location.search).get('redirect') ?? '/';

  if (sessionLoading) {
    return <div className="container-narrow py-12 text-center text-sm text-(--color-muted-foreground)">Caricamento…</div>;
  }
  if (session) {
    return <Navigate to={redirect} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await signIn.mutateAsync({ username, password });
      navigate(redirect, { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore di accesso';
      setError(msg);
    }
  }

  return (
    <div className="container-narrow py-16 max-w-md flex flex-col gap-8">
      <img
        src="/ceccarivs-bg.png"
        alt="Ceccarius"
        className="order-last sm:order-first mx-auto block w-56 max-w-full drop-shadow-sm"
      />
      <Card className="overflow-hidden">
        <CardContent className="bg-(--color-brand-sage)/25 pt-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                minLength={1}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <p className="text-sm text-(--color-destructive)" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={signIn.isPending}>
              {signIn.isPending ? 'Accesso in corso…' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
